import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Plus, Loader2, Save, Trash2, X, Check,
  Zap, ChevronUp, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem("session_token")}` };
}

export default function QuickReplies() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // template_id or "new"
  const [draftLabel, setDraftLabel] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/quick-replies`, { headers: auth() });
      setItems(res.data || []);
    } catch (err) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setDraftLabel("");
    setDraftBody("");
  };

  const startEdit = (t) => {
    setEditing(t.template_id);
    setDraftLabel(t.label);
    setDraftBody(t.body);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftLabel("");
    setDraftBody("");
  };

  const saveDraft = async () => {
    if (!draftLabel.trim() || !draftBody.trim()) {
      toast.error("Both label and body are required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/quick-replies`, { label: draftLabel.trim(), body: draftBody.trim() }, { headers: auth() });
        toast.success("Template created");
      } else {
        await axios.put(`${API}/quick-replies/${editing}`, { label: draftLabel.trim(), body: draftBody.trim() }, { headers: auth() });
        toast.success("Template updated");
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (t) => {
    try {
      await axios.put(`${API}/quick-replies/${t.template_id}`, { enabled: !t.enabled }, { headers: auth() });
      await load();
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`Delete "${t.label}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/quick-replies/${t.template_id}`, { headers: auth() });
      toast.success("Template deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const move = async (idx, delta) => {
    const next = idx + delta;
    if (next < 0 || next >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setItems(reordered);
    try {
      await axios.post(`${API}/quick-replies/reorder`,
        { template_ids: reordered.map((t) => t.template_id) },
        { headers: auth() });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-4">
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            Quick Reply Templates
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Canned text replies you can one-tap insert in the Messages page. Add things like "On my way", "Running late", or "Thanks for booking!" so you don't have to retype every time.
          </p>
        </div>

        {/* Merge-tag hint */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 mb-4">
          <strong>Tip:</strong> Use <span className="font-mono">{"{first_name}"}</span> in the body — it auto-fills the customer's first name when you insert the template.
        </div>

        {/* New / Add button */}
        {editing !== "new" && (
          <button
            onClick={startNew}
            data-testid="add-template-btn"
            className="w-full bg-white border-2 border-dashed border-zinc-300 hover:border-[#207024] hover:text-[#207024] text-zinc-500 rounded-xl py-3 mb-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" /> Add new template
          </button>
        )}

        {/* New template editor (shown above the list) */}
        {editing === "new" && (
          <DraftCard
            isNew
            label={draftLabel}
            body={draftBody}
            saving={saving}
            onLabel={setDraftLabel}
            onBody={setDraftBody}
            onSave={saveDraft}
            onCancel={cancelEdit}
          />
        )}

        {/* List */}
        <div className="space-y-2">
          {items.length === 0 && editing !== "new" && (
            <div className="bg-white rounded-xl border-2 border-zinc-100 p-8 text-center text-zinc-500 text-sm">
              <Zap className="w-6 h-6 mx-auto mb-2 text-zinc-300" />
              No templates yet. Tap <strong>Add new template</strong> above to create your first one.
            </div>
          )}
          {items.map((t, idx) => editing === t.template_id ? (
            <DraftCard
              key={t.template_id}
              label={draftLabel}
              body={draftBody}
              saving={saving}
              onLabel={setDraftLabel}
              onBody={setDraftBody}
              onSave={saveDraft}
              onCancel={cancelEdit}
            />
          ) : (
            <div
              key={t.template_id}
              data-testid={`template-row-${t.template_id}`}
              className={`bg-white rounded-xl border-2 p-4 transition-opacity ${
                t.enabled ? "border-zinc-100" : "border-zinc-100 opacity-60"
              }`}
            >
              <div className="flex items-start gap-3 mb-2">
                {/* Reorder arrows */}
                <div className="flex flex-col -my-1">
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    className="p-0.5 text-zinc-400 hover:text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-900 truncate" data-testid={`template-label-${t.template_id}`}>
                    {t.label}
                  </div>
                  <div className="text-sm text-zinc-600 whitespace-pre-wrap break-words mt-0.5">
                    {t.body}
                  </div>
                </div>

                {/* Enable / disable */}
                <button
                  type="button"
                  onClick={() => toggleEnabled(t)}
                  data-testid={`toggle-enabled-${t.template_id}`}
                  aria-checked={t.enabled}
                  role="switch"
                  className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${
                    t.enabled ? "bg-[#207024]" : "bg-zinc-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow flex items-center justify-center transition-transform ${
                      t.enabled ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  >
                    {t.enabled && <Check className="w-2.5 h-2.5 text-[#207024]" />}
                  </span>
                </button>
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => startEdit(t)}
                  data-testid={`edit-${t.template_id}`}
                  className="text-xs text-zinc-600 hover:text-zinc-900 font-medium"
                >
                  Edit
                </button>
                <span className="text-zinc-200">•</span>
                <button
                  onClick={() => removeTemplate(t)}
                  data-testid={`delete-${t.template_id}`}
                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
                <span className="ml-auto text-[11px] text-zinc-400">
                  {t.enabled ? "Visible in Messages" : "Hidden"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DraftCard({ isNew, label, body, saving, onLabel, onBody, onSave, onCancel }) {
  return (
    <div className="bg-white rounded-xl border-2 border-[#207024]/30 p-4 mb-2" data-testid={isNew ? "draft-new" : "draft-edit"}>
      <input
        type="text"
        placeholder="Label (e.g., On my way)"
        value={label}
        maxLength={80}
        onChange={(e) => onLabel(e.target.value)}
        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm font-medium mb-2 focus:outline-none focus:ring-2 focus:ring-[#207024]/30"
        data-testid="draft-label-input"
      />
      <textarea
        placeholder="Hey {first_name}, I'm on my way 👋"
        value={body}
        maxLength={1000}
        rows={3}
        onChange={(e) => onBody(e.target.value)}
        className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#207024]/30 resize-y"
        data-testid="draft-body-input"
      />
      <div className="flex gap-2 mt-2">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="draft-save-btn"
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#207024] text-white hover:bg-[#185a1c] disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isNew ? "Create" : "Save"}
        </button>
        <button
          onClick={onCancel}
          data-testid="draft-cancel-btn"
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
        >
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <span className="ml-auto text-[11px] text-zinc-400 self-center">
          {body.length}/1000
        </span>
      </div>
    </div>
  );
}
